// Simple HTML sanitization utility to prevent XSS attacks
// This is a minimal implementation - consider using DOMPurify for production

/**
 * Sanitizes HTML content by removing potentially dangerous tags and attributes
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHTML = (html) => {
    if (!html || typeof html !== 'string') {
        return '';
    }

    // Remove script tags and their content
    let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    // Remove other potentially dangerous tags
    const dangerousTags = [
        'javascript:',
        'vbscript:',
        'onload',
        'onerror',
        'onclick',
        'onmouseover',
        'onfocus',
        'onblur',
        'onchange',
        'onsubmit',
        'onreset',
        'onselect',
        'onunload',
        'onbeforeunload',
        'onresize',
        'onmove',
        'onabort',
        'onbeforeprint',
        'onafterprint',
        'onbeforeprint',
        'onafterprint',
    ];

    dangerousTags.forEach((tag) => {
        const regex = new RegExp(tag, 'gi');
        sanitized = sanitized.replace(regex, '');
    });

    // Remove any remaining event handlers
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');

    // Remove data: URLs which can be used for XSS
    sanitized = sanitized.replace(/data:\s*[^;]*;base64,.*?["']/gi, '');

    return sanitized;
};

/**
 * Validates and sanitizes URLs to prevent open redirect attacks
 * @param {string} url - The URL to validate
 * @param {string[]} allowedDomains - Array of allowed domains
 * @returns {string|null} - Sanitized URL or null if invalid
 */
export const validateAndSanitizeURL = (url, allowedDomains = []) => {
    if (!url || typeof url !== 'string') {
        return null;
    }

    try {
        // If it's a relative URL, it's safe
        if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
            return url;
        }

        // If it's a full URL, validate the domain
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();

        // Check if domain is in allowed list
        if (allowedDomains.length > 0) {
            const isAllowed = allowedDomains.some(
                (domain) => hostname === domain.toLowerCase() || hostname.endsWith('.' + domain.toLowerCase()),
            );

            if (!isAllowed) {
                return null;
            }
        }

        // Only allow http and https protocols
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
            return null;
        }

        return url;
    } catch (error) {
        // Invalid URL
        return null;
    }
};
